import type { MouseEvent } from 'react'
import { useLocation, useNavigate } from 'react-router'

/** Parámetros que la app actual recibe en la URL y que v2 conserva en toda navegación (inventario D16, D26). */
export const QUERY = { guestCode: 'guestcode', lang: 'langid', device: 'device' } as const

export function readQuery(search: string = typeof window === 'undefined' ? '' : window.location.search) {
  const p = new URLSearchParams(search)
  return {
    guestCode: p.get(QUERY.guestCode)?.trim() ?? '',
    lang: p.get(QUERY.lang),
    device: p.get(QUERY.device),
  }
}

/** Cambios en la query al navegar: un valor lo pone, `null` lo quita (p. ej. el parámetro de retorno `volver`). */
export type QueryChanges = Record<string, string | null>

export function mergeQuery(search: string, changes?: QueryChanges): string {
  if (!changes) return search
  const p = new URLSearchParams(search)
  for (const [key, value] of Object.entries(changes)) {
    if (value === null) p.delete(key)
    else p.set(key, value)
  }
  const out = p.toString()
  return out ? `?${out}` : ''
}

/** Portado de useRouter/useAppNavigate de la app actual: navegar conservando la query. */
export function useAppNavigate() {
  const navigate = useNavigate()
  const { search } = useLocation()
  return {
    /** `pathname` admite ancla (`/reserva#documentos`) para ir a una sección de la pantalla. */
    go: (target: string, options: { replace?: boolean; state?: unknown; query?: QueryChanges } = {}) => {
      const [pathname = '/', hash] = target.split('#')
      void navigate({ pathname, search: mergeQuery(search, options.query), ...(hash ? { hash: `#${hash}` } : {}) }, { replace: options.replace ?? false, state: options.state })
    },
    back: () => {
      void navigate(-1)
    },
    /** Para enlaces del sistema (`href` + `onSelect`): intercepta el click y navega con la query. */
    onLinkClick: (pathname: string) => (e: MouseEvent<HTMLElement>) => {
      e.preventDefault()
      void navigate({ pathname, search })
    },
    search,
  }
}

/** href con la query actual, para enlaces que no pasan por el router (p. ej. BottomNav). */
export function withQuery(target: string, search: string, changes?: QueryChanges): string {
  const [pathname = '/', hash] = target.split('#')
  return `${pathname}${mergeQuery(search, changes)}${hash ? `#${hash}` : ''}`
}
